import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import YouTube, { YouTubeProps } from 'react-youtube';
import { createPortal } from 'react-dom';
import Icon from 'core/assets/icons';
import './YouTubePlayer.css'

export type IProps = {
  videoId: string;
  onClose: () => void;
};

function convertSecondsToHMS(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;

    // Format with leading zeros if necessary
    const formattedHours = String(hours).padStart(2, '0');
    const formattedMinutes = String(minutes).padStart(2, '0');
    const formattedSeconds = String(remainingSeconds).padStart(2, '0');

    return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
}

export default function VideoYoutube ({
  onClose,
  videoId,
}: IProps) {
  const backRef = useRef<any>(null);
  const playerRef = useRef<any>(null);
  const playerWrapperRef = useRef<any>(null);
  const timerRef = useRef<any>(null);
  const [isInit, setIsInit] = useState<boolean>(false);
  const [isShowControl, setIsShowControl] = useState<boolean>(false);
  const [isPlaying, setIsPlaying] = useState<boolean>(true);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState<boolean>(true);
  const [volume, setVolume] = useState(100);
  const [isMiniPlayer, setIsMiniPlayer] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const miniPlayerSize = useMemo(() => ({ width: 250, height: 160 }), []); // Adjust according to your mini-player size

  const onReady: YouTubeProps['onReady'] = (event) => {
    playerRef.current = event.target;
    playerRef.current.mute();
    
    onClickWrapper()
    toggleFullScreen()
  
    playerRef.current.playVideo();
    setDuration(playerRef.current.getDuration());
    startUpdateProgress();
    setIsInit(true);
  };

  const onClickWrapper = () => {
    setIsShowControl(true)
    if (timerRef.current) {
      clearTimeout(timerRef.current)
    }
    timerRef.current = setTimeout(() => {
      setIsShowControl(false)
    }, 3000)
  }

  useEffect(() => {
    backRef.current = (window as any).backFromDeviceAndroid;

    (window as any).backFromDeviceAndroid = () => {
      onClose()
    }

    return () => {
      (window as any).backFromDeviceAndroid = backRef.current
    }
  }, [])

  const toggleFullScreen = () => {
    if (playerWrapperRef.current) {
      setIsFullscreen((isFullscreen: boolean) => !isFullscreen);
    }
  };

  const handleFullScreenChange = useCallback(() => {
    if (!document.fullscreenElement) {
      // Exited full screen: Stop and reset video
      playerRef.current?.pauseVideo();
      playerRef.current?.seekTo(0);
      onClose()
    }
  }, [playerRef, onClose]);

  useEffect(() => {
    // Add event listener for full screen change
    document.addEventListener('fullscreenchange', handleFullScreenChange);

    return () => {
      // Cleanup event listener on component unmount
      document.removeEventListener('fullscreenchange', handleFullScreenChange);
      document.body.classList.remove('overflow-hidden'); // Clean up on unmount
    };
  }, [handleFullScreenChange]);

  useEffect(() => {
    if (isFullscreen) {
      document.body.classList.add('overflow-hidden');
      // document.getElementById('app')?.classList?.add('hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
      // document.getElementById('app')?.classList?.remove('hidden');
    }

    return () => {
      // document.getElementById('app')?.classList?.remove('hidden');
    }
  }, [isFullscreen]);

  const handlePlayPause = () => {
    if (isPlaying) {
      playerRef.current.pauseVideo();
    } else {
      playerRef.current.playVideo();
    }
    setIsPlaying(!isPlaying);
  };

  const handleMute = () => {
    if (playerRef.current) {
      if (isMuted) {
        playerRef.current.unMute();
        playerRef.current.setVolume(volume); // Restore the previous volume level
      } else {
        playerRef.current.mute();
      }
      setIsMuted(!isMuted);
    }
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = Number(e.target.value);
    setVolume(newVolume);
    if (playerRef.current) {
      playerRef.current.setVolume(newVolume);
      
      // If the volume is set to 0, mute the player
      if (newVolume === 0) {
        playerRef.current.mute();
        setIsMuted(true);
      } else {
        // If unmuting, also ensure the volume is restored
        if (isMuted) {
          playerRef.current.unMute(); // Unmute if the slider is adjusted
          setIsMuted(false);
        }
      }
    }
  };

  const seekTo = (second: number) => {
    playerRef.current.seekTo(playerRef.current.getCurrentTime() + second);
  };

  const startUpdateProgress = () => {
    const interval = setInterval(() => {
      if (playerRef.current) {
        setCurrentTime(playerRef.current.getCurrentTime()?.toFixed(0) || 0);
      }
    }, 500); // Update every second

    return interval;
  };

  const handleProgressBarClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const offsetX = e.clientX - rect.left; // X position of the click inside the progress bar
    const newTime = (offsetX / rect.width) * duration; // Calculate the new time based on the click position
    playerRef.current.seekTo(newTime); // Seek to the new time
  };

  useEffect(() => {
    // Set initial position to bottom-right corner when mini-player is enabled
    if (isMiniPlayer) {
      const startX = window.innerWidth - miniPlayerSize.width - 10; // 10px margin from the right
      const startY = window.innerHeight - miniPlayerSize.height - 10; // 10px margin from the bottom
      setPosition({ x: startX, y: startY });
    }
  }, [isMiniPlayer, miniPlayerSize]);

  const toggleMiniPlayer = () => {
    setIsMiniPlayer(!isMiniPlayer);
    setIsFullscreen(!isFullscreen);
  };

  const resetPosition = useCallback(() => {
    if (isMiniPlayer) {
      const startX = window.innerWidth - miniPlayerSize.width - 10;
      const startY = window.innerHeight - miniPlayerSize.height - 10;
      setPosition({ x: startX, y: startY });
    }
  }, [setPosition, miniPlayerSize, isMiniPlayer]);

  useEffect(() => {
    const handleResize = () => {
      resetPosition();
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isMiniPlayer, resetPosition]);

  const handleDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    if (isMiniPlayer) {
      setDragging(true);
      document.body.classList.add('overflow-hidden'); // Disable body scroll
      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
      setStartPos({ x: clientX - position.x, y: clientY - position.y });
    }
  };

  const handleDragMove = (e: React.MouseEvent | React.TouchEvent) => {
    if (dragging) {
      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

      const newX = clientX - startPos.x;
      const newY = clientY - startPos.y;

      // Calculate the screen bounds for the mini-player
      const maxX = window.innerWidth - miniPlayerSize.width;
      const maxY = window.innerHeight - miniPlayerSize.height;

      // Restrict newX and newY within bounds
      const boundedX = Math.max(0, Math.min(newX, maxX));
      const boundedY = Math.max(0, Math.min(newY, maxY));

      setPosition({ x: boundedX, y: boundedY });
    }
  };

  const handleDragEnd = () => {
    setDragging(false);
    document.body.classList.remove('overflow-hidden'); // Re-enable body scroll
  };

  return createPortal(
    <div
      ref={playerWrapperRef}
      className={`
        youtube-player-wrapper bg-dark
        ${isMiniPlayer ? 'mini-player' : ''}
        ${!isPlaying ? 'paused' : ''}
      `}
      onClick={onClickWrapper}
      style={!isInit ? {
        position: 'fixed',
        width: '100vw',
        height: '100vh',
      } : {
        position: isFullscreen && !isMiniPlayer ? 'fixed' : 'fixed',
        top: isFullscreen && !isMiniPlayer ? '0' : `${position.y}px`,
        left: isFullscreen && !isMiniPlayer ? '0' : `${position.x}px`,
        width: isFullscreen && !isMiniPlayer ? '100vw' : `${miniPlayerSize.width}px`,
        height: isFullscreen && !isMiniPlayer ? '100vh' : `${miniPlayerSize.height}px`,
        zIndex: isFullscreen && !isMiniPlayer ? 9999 : '9999',
      }}
    >
      <div>
        <YouTube
          videoId={videoId}
          onReady={onReady}
          opts={{
            height: '0',
            width: '0',
            playerVars: {
              autoplay: 0,
              controls: 0, // Hide default controls
              modestbranding: 1, // Minimizes YouTube logo
              showinfo: 0, // Hides video title
              rel: 0, // No related videos at end
              iv_load_policy: 3, // No annotations
              playsinline: 1,
              disablekb: 1,
              cc_load_policy: 0,
              cc_lang_pref: 0,
              autohide: 1,
              html5: 1,
            },
          }}
        />
        <div
          className={`absolute top-0 left-0 w-full h-full p-4`}
          onMouseDown={handleDragStart}
          onMouseMove={handleDragMove}
          onMouseUp={handleDragEnd}
          onTouchStart={handleDragStart}
          onTouchMove={handleDragMove}
          onTouchEnd={handleDragEnd}
        >
          <div className={`relative w-full h-full ${!isShowControl ? 'hidden' : ''}`}>
            <div className="absolute top-0 left-0">
              <button
                onClick={onClose}
              >
                <img
                  className="w-[40px] h-[40px]"
                  src={Icon.VideoRemove}
                  alt={'remove'}
                />
              </button>

              <button className="control-button" onClick={toggleMiniPlayer}>
                
                <img
                  className="w-[32px] h-[32px]"
                  src={isMiniPlayer ? Icon.VideoFullPlayer : Icon.VideoMiniPlayer}
                  alt={'remove'}
                />
              </button>
            </div>
            {!isMiniPlayer && (
              <div className="absolute top-0 right-0">
                <div className="flex justify-center items-center">
                  <div className="volume-control">
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={isMuted ? 0 : volume} // Show 0 if muted
                      onChange={handleVolumeChange}
                      className="volume-slider"
                    />
                  </div>
                  <button
                    onClick={handleMute}
                  >
                    <img
                      className="w-[40px] h-[40px]"
                      src={isMuted ? Icon.VideoMute : Icon.VideoUnmute }
                      alt={'mute'}
                    />
                  </button>
                </div>
              </div>
            )}
            
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
              {!isMiniPlayer && (
                <div className="w-full flex gap-10">
                  <button
                    onClick={() => seekTo(-10)}
                  >
                    <img
                      className="w-[40px] h-[40px]"
                      src={Icon.VideoPrev10}
                      alt={'next10'}
                    />
                  </button>
                  <button
                    onClick={handlePlayPause}
                  >
                    <img
                      className="w-[40px] h-[40px]"
                      src={isPlaying ? Icon.VideoPause : Icon.VideoPlay}
                      alt={isPlaying ? 'VideoPause' : 'VideoPlay'}
                    />
                    
                  </button>
                  <button
                    onClick={() => seekTo(10)}
                  >
                    <img
                      className="w-[40px] h-[40px]"
                      src={Icon.VideoNext10}
                      alt={'next10'}
                    />
                  </button>
                </div>
              )}
            </div>
            {!isMiniPlayer && (
              <div className="progress-bar" onClick={handleProgressBarClick}>
                <div
                  className="progress-bar-filled"
                  style={{ width: `${(currentTime / duration) * 100}%` }}
                />
              </div>
              )}
            {!isMiniPlayer && (
              <div className="absolute bottom-1 w-full">
                <div className="flex justify-between text-white w-full">
                  <span>{convertSecondsToHMS(playerRef.current?.getCurrentTime()?.toFixed(0))}</span>
                  <span>- {convertSecondsToHMS(playerRef.current?.getDuration()?.toFixed(0) - playerRef.current?.getCurrentTime()?.toFixed(0))}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
